import { ExternalLink, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { adminClient, type AdminOrgBilling, type AdminPriceRow } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminMetaItem } from '@/components/shell/AdminMetaItem';
import { DataTable, DataTableCell, DataTableHead, DataTableMessage, DataTableRow } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet } from '@/components/ui/sheet';
import { formatDate, formatUsd } from '@/lib/format';
import { planStatusPill } from '@/lib/orgMoney';
import { humanizeKey } from '@/lib/runTelemetry';

type PromoKind = 'percent' | 'amount' | 'coupon' | 'trial';

const INVOICE_COLUMNS = [
  { label: 'Invoice' },
  { label: 'Status' },
  { label: 'Amount', className: 'text-right' },
  { label: 'Date', className: 'text-right' },
];

export function PlanPanel({
  orgId,
  billing,
  onBillingChange,
  onClose,
}: {
  orgId: string;
  billing?: AdminOrgBilling;
  onBillingChange: (billing: AdminOrgBilling) => void;
  onClose: () => void;
}) {
  const [prices, setPrices] = useState<AdminPriceRow[]>([]);
  const [priceId, setPriceId] = useState(billing?.priceId ?? '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void adminClient
      .listPrices()
      .catch(() => ({ items: [] as AdminPriceRow[] }))
      .then((next) => {
        setPrices(next.items);
        setPriceId((current) => current || next.items[0]?.priceId || '');
      });
  }, []);

  const run = (action: () => Promise<void>, failure: string) => {
    setBusy(true);
    action()
      .catch((err: unknown) => toast.error(err instanceof Error ? err.message : failure))
      .finally(() => setBusy(false));
  };

  const linkCustomer = () =>
    run(async () => {
      onBillingChange(await adminClient.ensureCustomer(orgId));
      toast.success('Stripe customer linked');
    }, 'Failed to link the Stripe customer');

  const openCheckout = () =>
    run(async () => {
      const { url } = await adminClient.createCheckout(orgId, priceId);
      onClose();
      window.location.href = url;
    }, 'Failed to start checkout');

  const hasCustomer = Boolean(billing?.stripeCustomerId);
  const status = planStatusPill(billing);

  return (
    <Sheet
      open
      onClose={onClose}
      title="Plan"
      description="What this organization pays for in Stripe. Checkout opens Stripe to start or change the plan."
    >
      <dl className="grid gap-4 sm:grid-cols-2">
        <AdminMetaItem label="Plan">{billing?.productName || 'No plan'}</AdminMetaItem>
        <AdminMetaItem label="Status">
          <StatusPill tone={status.tone}>{status.label}</StatusPill>
        </AdminMetaItem>
        <AdminMetaItem label={billing?.cancelAtPeriodEnd ? 'Ends' : 'Renews'}>
          {formatDate(billing?.currentPeriodEnd)}
        </AdminMetaItem>
        <AdminMetaItem label="Stripe customer">
          {hasCustomer ? (
            <span className="font-mono text-xs">{billing?.stripeCustomerId}</span>
          ) : (
            <span className="text-muted-foreground">Not linked</span>
          )}
        </AdminMetaItem>
        <AdminMetaItem label="Trial ends">{formatDate(billing?.trialEnd)}</AdminMetaItem>
        <AdminMetaItem label="Next checkout trial">
          {typeof billing?.trialPeriodDays === 'number'
            ? `${billing.trialPeriodDays} day${billing.trialPeriodDays === 1 ? '' : 's'}`
            : '—'}
        </AdminMetaItem>
      </dl>
      <div className="mt-5 border-t border-border pt-5">
        {hasCustomer ? (
          <div className="flex flex-col gap-3">
            <div>
              <Label htmlFor="checkout-price">Start or change plan</Label>
              <Select value={priceId} onValueChange={setPriceId}>
                <SelectTrigger id="checkout-price" className="mt-2">
                  <SelectValue placeholder="Select a Stripe price" />
                </SelectTrigger>
                <SelectContent>
                  {prices.map((price) => (
                    <SelectItem key={price.priceId} value={price.priceId}>
                      {price.productName} · {formatUsd((price.unitAmount ?? 0) / 100)}
                      {price.interval ? ` / ${price.interval}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="button" onClick={openCheckout} disabled={busy || !priceId} className="self-start">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Open checkout
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">Link a Stripe customer before starting a plan.</p>
            <Button type="button" onClick={linkCustomer} disabled={busy} className="self-start">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Link Stripe customer
            </Button>
          </div>
        )}
      </div>
    </Sheet>
  );
}

export function InvoicesPanel({ billing, onClose }: { billing?: AdminOrgBilling; onClose: () => void }) {
  const invoices = billing?.invoices ?? [];
  return (
    <Sheet open onClose={onClose} title="Invoices" description="From Stripe, newest first." className="sm:max-w-2xl">
      <DataTable>
        <DataTableHead columns={INVOICE_COLUMNS} />
        {invoices.length === 0 ? (
          <DataTableMessage colSpan={INVOICE_COLUMNS.length}>No invoices yet</DataTableMessage>
        ) : (
          <tbody>
            {invoices.map((invoice) => (
              <DataTableRow key={invoice.id}>
                <DataTableCell>
                  {invoice.hostedInvoiceUrl ? (
                    <a
                      href={invoice.hostedInvoiceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                    >
                      {invoice.number || invoice.id}
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    </a>
                  ) : (
                    <span className="font-medium">{invoice.number || invoice.id}</span>
                  )}
                </DataTableCell>
                <DataTableCell>
                  <StatusPill tone={invoice.status === 'paid' ? 'success' : invoice.status === 'open' ? 'warning' : 'neutral'}>
                    {humanizeKey(invoice.status || 'unknown')}
                  </StatusPill>
                </DataTableCell>
                <DataTableCell className="text-right tabular-nums">{formatUsd(invoice.amountPaid / 100)}</DataTableCell>
                <DataTableCell className="text-right text-muted-foreground">{formatDate(invoice.created)}</DataTableCell>
              </DataTableRow>
            ))}
          </tbody>
        )}
      </DataTable>
    </Sheet>
  );
}

export function PromoPanel({
  orgId,
  onClose,
  onApplied,
}: {
  orgId: string;
  onClose: () => void;
  onApplied: () => void;
}) {
  const [kind, setKind] = useState<PromoKind>('percent');
  const [percentOff, setPercentOff] = useState('20');
  const [amountOffUsd, setAmountOffUsd] = useState('50');
  const [couponId, setCouponId] = useState('');
  const [name, setName] = useState('');
  const [trialDays, setTrialDays] = useState('');
  const [generateCode, setGenerateCode] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const buildInput = (): Parameters<typeof adminClient.applyOrgPromo>[1] | string => {
    const input: Parameters<typeof adminClient.applyOrgPromo>[1] = {};
    if (kind === 'percent') {
      const value = Number(percentOff);
      if (!Number.isFinite(value) || value <= 0 || value > 100) return 'Percent off must be between 1 and 100';
      input.percentOff = value;
    } else if (kind === 'amount') {
      const value = Math.round(Number(amountOffUsd) * 100);
      if (!Number.isInteger(value) || value <= 0) return 'Amount off must be a positive dollar amount';
      input.amountOffCents = value;
    } else if (kind === 'coupon') {
      if (!couponId.trim()) return 'Enter the Stripe coupon id';
      input.couponId = couponId.trim();
    }
    if (kind === 'trial' || trialDays.trim()) {
      const days = Number(trialDays);
      if (!Number.isInteger(days) || days < 0 || days > 730) return 'Trial days must be a whole number from 0 to 730';
      input.trialPeriodDays = days;
    }
    if (kind !== 'trial') {
      if (name.trim()) input.name = name.trim();
      if (generateCode || code.trim()) {
        input.generatePromoCode = true;
        if (code.trim()) input.promoCode = code.trim();
      }
    }
    return input;
  };

  const submit = async () => {
    const input = buildInput();
    if (typeof input === 'string') {
      toast.error(input);
      return;
    }
    setBusy(true);
    try {
      const next = await adminClient.applyOrgPromo(orgId, input);
      toast.success(next.promoCode ? `Promotion applied. Code ${next.promoCode}` : 'Promotion applied');
      onApplied();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to apply the promotion');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title="Apply promotion"
      description="Discounts the Stripe invoice remainder after credits. Codes can be redeemed at checkout."
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="promotion-form" disabled={busy}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            Apply
          </Button>
        </>
      }
    >
      <form
        id="promotion-form"
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div>
          <Label htmlFor="promo-kind">Type</Label>
          <Select value={kind} onValueChange={(next) => setKind(next as PromoKind)}>
            <SelectTrigger id="promo-kind" className="mt-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="percent">Percent off</SelectItem>
              <SelectItem value="amount">Amount off</SelectItem>
              <SelectItem value="coupon">Existing Stripe coupon</SelectItem>
              <SelectItem value="trial">Trial days only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {kind === 'percent' ? (
            <div>
              <Label htmlFor="promo-percent">Percent off</Label>
              <Input
                id="promo-percent"
                className="mt-2"
                type="number"
                min="1"
                max="100"
                step="1"
                value={percentOff}
                onChange={(event) => setPercentOff(event.target.value)}
              />
            </div>
          ) : null}
          {kind === 'amount' ? (
            <div>
              <Label htmlFor="promo-amount">Amount off (USD)</Label>
              <Input
                id="promo-amount"
                className="mt-2"
                type="number"
                min="0.01"
                step="0.01"
                value={amountOffUsd}
                onChange={(event) => setAmountOffUsd(event.target.value)}
              />
            </div>
          ) : null}
          {kind === 'coupon' ? (
            <div>
              <Label htmlFor="promo-coupon">Coupon id</Label>
              <Input
                id="promo-coupon"
                className="mt-2"
                placeholder="ncz6FvhL"
                value={couponId}
                onChange={(event) => setCouponId(event.target.value)}
              />
            </div>
          ) : null}
          <div>
            <Label htmlFor="promo-trial">Trial days {kind === 'trial' ? '' : '(optional)'}</Label>
            <Input
              id="promo-trial"
              className="mt-2"
              type="number"
              min="0"
              max="730"
              step="1"
              placeholder="14"
              value={trialDays}
              onChange={(event) => setTrialDays(event.target.value)}
            />
          </div>
        </div>
        {kind !== 'trial' ? (
          <>
            <div>
              <Label htmlFor="promo-name">Coupon name (optional)</Label>
              <Input
                id="promo-name"
                className="mt-2"
                placeholder="Pilot 20% off"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={generateCode} onChange={(event) => setGenerateCode(event.target.checked)} />
              Create a promo code for checkout
            </label>
            {generateCode ? (
              <div>
                <Label htmlFor="promo-code">Code (optional)</Label>
                <Input
                  id="promo-code"
                  className="mt-2"
                  placeholder="SAVE20"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </div>
            ) : null}
          </>
        ) : null}
      </form>
    </Sheet>
  );
}
